import { CloudEvent, CloudEventV1Attributes } from 'cloudevents';
import { AuthContextStateProps } from '../hooks/useAuthentication';

type PartialCloudEvent = Pick<
  CloudEventV1Attributes,
  | 'type'
  | 'data'
  | 'data_base64'
  | 'datacontenttype'
  | 'dataschema'
  | 'subject'
  | 'time'
>;

export enum AuthType {
  request = 'agero-websocket-auth-request',
  response = 'agero-websocket-auth-response',
}

export enum InternalEventType {
  authAttempt = 'websocket-service-auth-attempt',
  error = 'websocket-service-error',
}

export interface SubscribeReturn {
  unsubscribe: () => void;
}

export interface WebsocketService {
  subscribe: (
    event: string,
    cb: (event: CloudEvent) => void,
  ) => SubscribeReturn;
  send: (event: PartialCloudEvent) => void;
}

type EventSubs = {
  [key: string]: ((event: CloudEvent) => void)[];
};

export const SERVICE_SOURCE = 'com.agero.websocket-service';

export class WebsocketServiceImpl implements WebsocketService {
  private subs: EventSubs = {};
  private ws: WebSocket;
  private auth: AuthContextStateProps;
  private authAttempts = 0;
  private isAuthComplete = false;
  private messageQueue: string[] = [];

  constructor(ws: WebSocket, auth: AuthContextStateProps) {
    this.ws = ws;
    this.auth = auth;
    this.setWebsocket(ws);
  }

  subscribe(event: string, cb: (event: CloudEvent) => void): SubscribeReturn {
    this.subs[event] = this.subs[event] || [];
    this.subs[event].push(cb);
    // The bitshift right is used to convert a -1 result into 4294967295, resulting in no elements being removed.
    // Positive integers from found results are not modified with the >>> 0
    return {
      unsubscribe: () =>
        this.subs[event].splice(this.subs[event].indexOf(cb) >>> 0, 1),
    };
  }

  emit(event: CloudEvent) {
    (this.subs[event.type] || []).forEach(fn => fn(event));
  }

  send(event: PartialCloudEvent): void {
    const { email } = this.auth.getDecodedIdToken();
    const ce = new CloudEvent({
      ...event,
      source: email,
    });
    const ce_json = ce.toJSON();
    this.sendInternal(JSON.stringify(ce_json));
  }

  private sendInternal(message: string): boolean {
    if (this.ws.readyState !== WebSocket.OPEN || !this.isAuthComplete) {
      this.messageQueue.push(message);
      return false;
    }

    this.ws.send(message);
    return true;
  }

  processQueue(): void {
    let success = true;
    let message = this.messageQueue.shift();
    while (success && message) {
      success = this.sendInternal(message);
      message = this.messageQueue.shift();
    }
  }

  setWebsocket(socket: WebSocket) {
    this.isAuthComplete = false;
    this.ws = socket;
    this.ws.onopen = this.authenticate.bind(this);
    this.ws.onclose = () => {
      const ce = new CloudEvent({
        source: SERVICE_SOURCE,
        type: InternalEventType.error,
        data: 'Websocket closed',
      });
      this.emit(ce);
    };
    this.ws.onmessage = this.handleMessage.bind(this);
  }

  handleMessage(event: MessageEvent<any>): void {
    try {
      const received = JSON.parse(event.data);
      const ce = new CloudEvent(received);
      if (ce.type === AuthType.request) {
        this.isAuthComplete = false;
        this.authenticate();
      } else {
        this.emit(ce);
      }
    } catch (e) {
      const errorEvent = new CloudEvent({
        source: 'client',
        type: InternalEventType.error,
        data: 'Error parsing message',
      });
      this.emit(errorEvent);
    }
  }

  authenticate(): void {
    this.authAttempts++;
    const authAttemptEvent = new CloudEvent({
      type: InternalEventType.authAttempt,
      source: SERVICE_SOURCE,
      data: {
        attemptNumber: this.authAttempts,
      },
    });
    this.emit(authAttemptEvent);
    if (!this.auth.isUserSignedIn()) {
      setTimeout(() => this.authenticate(), Math.pow(50, this.authAttempts));
      return;
    }
    this.actuallyAuthenticate();
    this.authAttempts = 0;
    this.isAuthComplete = true;
    this.processQueue();
  }

  actuallyAuthenticate(): void {
    const { email } = this.auth.getDecodedIdToken();
    const authEvent = new CloudEvent({
      type: AuthType.response,
      source: email,
      data: {
        id_token: this.auth.getRawIdToken(),
      },
    });
    this.ws.send(JSON.stringify(authEvent.toJSON()));
    this.emit(authEvent);
  }
}
